import { buildAuthHeaders } from "../forum/authHeaders";
import {
  NOTIFICATION_ACTIONS,
  NOTIFICATION_BULK_ACTIONS,
  normalizeNotification,
  normalizeNotificationFilters,
  normalizeNotificationPage,
  normalizeNotificationPreference,
  normalizeNotificationPreferences,
  normalizeUnreadCount,
} from "./notificationModel";

export const NOTIFICATIONS_ENDPOINT = "/api/notifications/";
export const NOTIFICATION_UNREAD_COUNT_ENDPOINT =
  "/api/notifications/unread-count/";
export const NOTIFICATION_BULK_ACTIONS_ENDPOINT =
  "/api/notifications/bulk-actions/";
export const NOTIFICATION_PREFERENCES_ENDPOINT =
  "/api/notification-preferences/";
export const NOTIFICATION_EMAIL_TEST_ENDPOINT = "/api/notifications/email/test/";

const IS_LOCAL_FRONTEND =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE_URL = IS_LOCAL_FRONTEND
  ? process.env.REACT_APP_API_BASE_URL_LOCAL || ""
  : process.env.REACT_APP_API_BASE_URL || "";

function apiUrl(path) {
  return API_BASE_URL ? `${API_BASE_URL}${path}` : path;
}

function publicErrorMessage(payload, fallback) {
  const message =
    typeof payload === "string"
      ? payload.trim()
      : String(payload?.detail || payload?.message || "").trim();
  if (message && !/proxy error|error occurred while trying to proxy|stack trace/i.test(message)) {
    return message;
  }
  return fallback;
}

async function readPayload(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_error) {
    return text;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const response = await fetch(apiUrl(path), {
    method,
    cache: "no-store",
    credentials: "same-origin",
    signal,
    headers: buildAuthHeaders({
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    }),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await readPayload(response);
  if (!response.ok) {
    const error = new Error(
      publicErrorMessage(
        payload,
        `Le centre de notifications est momentanément indisponible (${response.status}).`
      )
    );
    error.status = response.status;
    error.code = payload?.code || null;
    error.payload = payload;
    throw error;
  }
  return payload;
}

function requiredOpaqueId(value, label = "notification") {
  const id = String(value || "").trim();
  if (!id) throw new Error(`Identifiant de ${label} manquant.`);
  return id;
}

function notificationListPath(filters, cursor) {
  const normalized = normalizeNotificationFilters(filters);
  const params = new URLSearchParams();
  params.set("status", normalized.status);
  if (normalized.category) params.set("category", normalized.category);
  if (normalized.universe) params.set("universe", normalized.universe);
  if (cursor !== undefined && cursor !== null && String(cursor).trim()) {
    params.set("cursor", String(cursor).trim());
  }
  return `${NOTIFICATIONS_ENDPOINT}?${params.toString()}`;
}

export async function fetchNotifications(
  { filters = {}, cursor = null } = {},
  { signal } = {}
) {
  const payload = await request(notificationListPath(filters, cursor), { signal });
  return normalizeNotificationPage(payload);
}

export async function fetchNotificationUnreadCount({ signal } = {}) {
  return normalizeUnreadCount(
    await request(NOTIFICATION_UNREAD_COUNT_ENDPOINT, { signal })
  );
}

export async function patchNotification(
  publicId,
  action,
  extra = {},
  { signal } = {}
) {
  const id = requiredOpaqueId(publicId);
  if (!NOTIFICATION_ACTIONS.includes(action)) {
    throw new Error("Action de notification invalide.");
  }
  const payload = await request(
    `${NOTIFICATIONS_ENDPOINT}${encodeURIComponent(id)}/`,
    {
      method: "PATCH",
      body: { ...extra, action },
      signal,
    }
  );
  return normalizeNotification(payload);
}

export async function bulkUpdateNotifications(
  action,
  publicIds = [],
  { signal } = {}
) {
  if (!NOTIFICATION_BULK_ACTIONS.includes(action)) {
    throw new Error("Action groupée de notification invalide.");
  }
  const ids = Array.from(
    new Set((Array.isArray(publicIds) ? publicIds : []).map((id) => String(id || "").trim()))
  ).filter(Boolean);
  if (action !== "mark_all_read" && ids.length === 0) {
    throw new Error("Sélectionne au moins une notification.");
  }
  const payload = await request(NOTIFICATION_BULK_ACTIONS_ENDPOINT, {
    method: "POST",
    body: action === "mark_all_read" ? { action } : { action, ids },
    signal,
  });
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Réponse d’action groupée invalide.");
  }
  const notifications = Array.isArray(payload.notifications)
    ? payload.notifications.map(normalizeNotification)
    : [];
  const updated = Number(payload.updated);
  return {
    action: String(payload.action || action),
    updated: Number.isSafeInteger(updated) && updated >= 0 ? updated : notifications.length,
    notifications,
  };
}

export async function fetchNotificationPreferences({ signal } = {}) {
  return normalizeNotificationPreferences(
    await request(NOTIFICATION_PREFERENCES_ENDPOINT, { signal })
  );
}

export async function updateNotificationPreference(
  category,
  patch,
  { signal } = {}
) {
  const normalizedCategory = requiredOpaqueId(category, "catégorie");
  const allowedKeys = ["in_app_enabled", "push_enabled", "email_enabled", "email_frequency"];
  const body = Object.entries(patch || {}).reduce((result, [key, value]) => {
    const snakeKey = {
      inAppEnabled: "in_app_enabled",
      pushEnabled: "push_enabled",
      emailEnabled: "email_enabled",
      emailFrequency: "email_frequency",
    }[key] || key;
    if (allowedKeys.includes(snakeKey)) result[snakeKey] = value;
    return result;
  }, {});
  if (Object.keys(body).length === 0) {
    throw new Error("Aucune préférence de notification à modifier.");
  }
  const payload = await request(
    `${NOTIFICATION_PREFERENCES_ENDPOINT}${encodeURIComponent(normalizedCategory)}/`,
    { method: "PATCH", body, signal }
  );
  return normalizeNotificationPreference(payload);
}

export async function updateNotificationQuietHours(patch, { signal } = {}) {
  const body = Object.fromEntries(
    Object.entries(patch || {}).filter(([key]) => ["enabled", "start", "end"].includes(key))
  );
  if (!Object.keys(body).length) throw new Error("Aucune plage silencieuse à modifier.");
  return request(`${NOTIFICATION_PREFERENCES_ENDPOINT}quiet-hours/`, {
    method: "PATCH",
    body,
    signal,
  });
}

export async function sendNotificationEmailTest({ signal } = {}) {
  return request(NOTIFICATION_EMAIL_TEST_ENDPOINT, {
    method: "POST",
    body: {},
    signal,
  });
}
